import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Share2, Check, Link } from 'lucide-react';
import { useState } from 'react';

export default function InviteShareModal({ isOpen, onClose, inviteCode, circleName }) {
  const [copiedType, setCopiedType] = useState(null);

  const inviteUrl = `${window.location.origin}/join/${inviteCode}`;

  const copyCode = async () => {
    await navigator.clipboard.writeText(inviteCode);
    setCopiedType('code');
    setTimeout(() => setCopiedType(null), 2000);
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(inviteUrl);
    setCopiedType('link');
    setTimeout(() => setCopiedType(null), 2000);
  };

  const shareInvite = async () => {
    const text = `Join my Art Ping Pong circle "${circleName}"!\n${inviteUrl}`;
    if (navigator.share) {
      await navigator.share({ title: 'Join Art Ping Pong', text, url: inviteUrl });
    } else {
      await navigator.clipboard.writeText(text);
      setCopiedType('link');
      setTimeout(() => setCopiedType(null), 2000);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <Modal
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Invite to {circleName}</ModalTitle>
              <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
            </ModalHeader>

            <CodeBox>
              <CodeLabel>Invite Code</CodeLabel>
              <Code>{inviteCode}</Code>
            </CodeBox>

            <InviteLinkBox>
              <InviteLinkText>{inviteUrl}</InviteLinkText>
            </InviteLinkBox>

            <Actions>
              <ActionBtn onClick={copyCode}>
                {copiedType === 'code' ? <Check size={18} /> : <Copy size={18} />}
                {copiedType === 'code' ? 'Copied!' : 'Code'}
              </ActionBtn>
              <ActionBtn onClick={copyLink}>
                {copiedType === 'link' ? <Check size={18} /> : <Link size={18} />}
                {copiedType === 'link' ? 'Copied!' : 'Link'}
              </ActionBtn>
              <ActionBtn $primary onClick={shareInvite}>
                <Share2 size={18} />
                Share
              </ActionBtn>
            </Actions>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
}

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(16px + env(safe-area-inset-top, 0px)) calc(16px + env(safe-area-inset-right, 0px)) calc(16px + env(safe-area-inset-bottom, 0px)) calc(16px + env(safe-area-inset-left, 0px));
`;

const Modal = styled(motion.div)`
  width: min(360px, 100%);
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.radius.xl};
  padding: 24px;
  box-shadow: ${({ theme }) => theme.shadows.large};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
`;

const CloseBtn = styled.button`
  padding: 6px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CodeBox = styled.div`
  text-align: center;
  padding: 20px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  margin-bottom: 20px;
`;

const CodeLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const Code = styled.div`
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 4px;
  color: ${({ theme }) => theme.colors.accent};
  font-family: 'SF Mono', 'Menlo', monospace;
`;

const InviteLinkBox = styled.div`
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 20px;
`;

const InviteLinkText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-all;
  font-family: 'SF Mono', 'Menlo', monospace;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.medium};
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s;
  background: ${({ $primary, theme }) =>
    $primary ? theme.colors.accent : theme.colors.surface};
  color: ${({ $primary, theme }) =>
    $primary ? 'white' : theme.colors.textPrimary};

  &:hover {
    transform: translateY(-1px);
  }
`;
